/** Project files: a project's Documents and its Sensor Docs (spec
 *  2026-10-21-project-documents-design). What a file is called and badged,
 *  what is refused before it is sent, and the sensor picker's sums. The
 *  server decides again; these only spare a reader a wasted upload and
 *  keep the picker's counts honest. */
import type { FileFamily, Sensor, SensorDoc } from '../api/types'
import { searchTerms } from './stationSearch'

export const MAX_MB = 200

const FAMILIES: Record<string, FileFamily> = {
  pdf: 'pdf',
  doc: 'word', docx: 'word', odt: 'word', rtf: 'word',
  xls: 'excel', xlsx: 'excel', ods: 'excel', csv: 'excel',
  dwg: 'drawing', dxf: 'drawing', dgn: 'drawing',
  jpg: 'image', jpeg: 'image', png: 'image', webp: 'image', gif: 'image', tif: 'image',
  tiff: 'image', heic: 'image', svg: 'image',
  zip: 'archive', '7z': 'archive', rar: 'archive',
}
// The backend's list (services/stored_files.py BLOCKED); it refuses them again.
const BLOCKED = new Set(['exe', 'msi', 'msp', 'bat', 'cmd', 'com', 'scr', 'pif', 'cpl', 'ps1',
                         'psm1', 'vbs', 'vbe', 'js', 'jse', 'wsf', 'hta', 'jar', 'sh', 'app',
                         'dll', 'lnk', 'reg'])

export const BADGE: Record<FileFamily, string> = {
  pdf: 'PDF', word: 'DOC', excel: 'XLS', drawing: 'DWG', image: 'IMG', archive: 'ZIP', other: 'FILE',
}
export const FAMILY_LABEL: Record<FileFamily, string> = {
  pdf: 'PDF', word: 'Word', excel: 'Excel', drawing: 'Drawing', image: 'Image', archive: 'Archive',
  other: 'Other',
}
export const FAMILY_ORDER: FileFamily[] = ['pdf', 'word', 'excel', 'drawing', 'image', 'archive',
                                          'other']

function extension(name: string): string {
  // Windows drops trailing dots and spaces when it saves a file, so read the
  // name as it would: "tool.exe." is an .exe.
  const saved = name.replace(/[. ]+$/, '')
  const dot = saved.lastIndexOf('.')
  return dot > 0 ? saved.slice(dot + 1).toLowerCase() : ''
}

export function familyOf(name: string): FileFamily {
  return FAMILIES[extension(name)] ?? 'other'
}

/** Why a document will not be kept, before it is sent, or null. The
 *  sentences are the server's. */
export function documentRefusal(file: { name: string; size: number }, maxMb = MAX_MB): string | null {
  if (BLOCKED.has(extension(file.name))) {
    return `${file.name} is a program or script, which this platform does not keep.`
  }
  if (file.size > maxMb * 1_000_000) return `${file.name} is larger than ${maxMb} MB.`
  if (file.size === 0) return `${file.name} is empty.`
  return null
}

function matches(text: string, query: string): boolean {
  const hay = searchTerms(text).join(' ')
  return searchTerms(query).every((term) => hay.includes(term))
}

export function filterDocs<T extends { name: string; family: FileFamily }>(
  docs: T[], query: string, family: FileFamily | ''): T[] {
  return docs.filter((d) => (!family || d.family === family) && matches(d.name, query))
}

// ------------------------------------------------------------------ picker

export interface PickerFilter {
  query: string
  station: string
  /** '' for any, a sensor type, or 'unset' for the many nobody has typed. */
  type: string
}

export const refOf = (s: Sensor): string => `${s.station.code}/${s.key}`

export function filterSensors(sensors: Sensor[], f: PickerFilter): Sensor[] {
  return sensors.filter((s) =>
    (!f.station || s.station.code === f.station)
    && (!f.type || (f.type === 'unset' ? s.sensor_type === null : s.sensor_type === f.type))
    && matches(`${s.name} ${s.model ?? ''} ${s.sensor_type ?? ''} ${s.station.name} ${s.key}`,
               f.query))
}

export interface StationGroup {
  code: string
  name: string
  sensors: Sensor[]
  state: 'none' | 'some' | 'all'
  selected: number
}

/** The shown sensors by station, in the order they came, each station with
 *  how many of its shown sensors are ticked. */
export function groupByStation(shown: Sensor[], chosen: ReadonlySet<string>): StationGroup[] {
  const groups = new Map<string, { code: string; name: string; sensors: Sensor[] }>()
  for (const s of shown) {
    const group = groups.get(s.station.code)
      ?? { code: s.station.code, name: s.station.name, sensors: [] }
    groups.set(s.station.code, { ...group, sensors: [...group.sensors, s] })
  }
  return [...groups.values()].map((g) => {
    const selected = g.sensors.filter((s) => chosen.has(refOf(s))).length
    const state = selected === 0 ? 'none' : selected === g.sensors.length ? 'all' : 'some'
    return { ...g, selected, state }
  })
}

/** A station's sensors are listed once it is opened, or while a search is
 *  typed (the search is how a reader finds a sensor inside a folded
 *  station). Ticking never opens one: "Select all" would unfold them all. */
export function isUnfolded(code: string, query: string, opened: ReadonlySet<string>): boolean {
  return query.trim() !== '' || opened.has(code)
}

/** A new set with the key added, or taken out if it was there. */
export function toggleKey(set: ReadonlySet<string>, key: string): Set<string> {
  const next = new Set(set)
  if (next.has(key)) next.delete(key)
  else next.add(key)
  return next
}

/** A new set with the shown sensors ticked (on) or unticked. */
export function selectShown(chosen: ReadonlySet<string>, shown: Sensor[], on: boolean): Set<string> {
  const next = new Set(chosen)
  for (const s of shown) {
    if (on) next.add(refOf(s))
    else next.delete(refOf(s))
  }
  return next
}

export function diff(initial: ReadonlySet<string>, chosen: ReadonlySet<string>): { add: string[]; remove: string[] } {
  return { add: [...chosen].filter((r) => !initial.has(r)),
           remove: [...initial].filter((r) => !chosen.has(r)) }
}

const sensorsWord = (n: number) => `${n} sensor${n === 1 ? '' : 's'}`

export function attachLabel(n: number): string {
  return `Attach to ${sensorsWord(n)}`
}

export function changeLabel(add: number, remove: number): string {
  const parts = [add ? `Add to ${add}` : '', remove ? `take off ${remove}` : ''].filter(Boolean)
  const text = parts.join(', ')
  return text ? text[0]!.toUpperCase() + text.slice(1) : 'No change'
}

/** What a change of holders did, said after it is done. */
export function changeNote(added: number, removed: number, already: number): string {
  const done = [added ? `Added to ${added}` : '', removed ? `taken off ${removed}` : '']
    .filter(Boolean).join(', ')
  const head = done ? done[0]!.toUpperCase() + done.slice(1) : 'Nothing changed'
  return already ? `${head}; ${already} already had it.` : `${head}.`
}

/** How many sensors are ticked and at which stations, largest first. */
export function stationsSummary(chosen: ReadonlySet<string>, sensors: Sensor[]): {
  count: number; stations: { name: string; n: number }[] } {
  const counts = new Map<string, number>()
  for (const s of sensors) {
    if (chosen.has(refOf(s))) counts.set(s.station.name, (counts.get(s.station.name) ?? 0) + 1)
  }
  const stations = [...counts].map(([name, n]) => ({ name, n })).sort((a, b) => b.n - a.n)
  return { count: stations.reduce((sum, s) => sum + s.n, 0), stations }
}

/** A sensor already holds a file of this name and size: worth saying before
 *  an upload (the server compares the bytes and has the last word). */
export function alreadyHas(docs: Pick<SensorDoc, 'name' | 'bytes' | 'sensors'>[], ref: string,
                           file: { name: string; size: number }): boolean {
  return docs.some((d) => d.name === file.name && d.bytes === file.size
                          && d.sensors.some((s) => s.ref === ref))
}
